import { Formik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiFileText, FiInfo } from "react-icons/fi";
import { FormField } from "@/components/common/form-field";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { getOfferSummary, getWorksheet, patchWorksheet, taxDebtsApi } from "@/features/worksheets/api";
import { AutosaveForm } from "@/features/worksheets/components/autosave-form";
import { CollectionCard, type Column } from "@/features/worksheets/components/collection-card";
import { IAWorksheetDialog } from "@/features/worksheets/components/dialogs/ia-worksheet-dialog";
import { PaymentScheduleDialog } from "@/features/worksheets/components/dialogs/payment-schedule-dialog";
import { TaxDebtDialog } from "@/features/worksheets/components/dialogs/tax-debt-dialog";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import { formatMoney } from "@/features/worksheets/money";
import type { OfferSummary, TaxDebt } from "@/features/worksheets/types";

/** Editable OIC offer plan (months of future income) + explanation. Autosaves
 * to the worksheet header; `onChanged` re-fetches the summary so the offer
 * amount recomputes. */
function OfferPlanCard({ caseId, onChanged }: { caseId: string; onChanged: () => void }) {
  const [initial, setInitial] = useState<{ offerPlan: string; explanation: string } | null>(null);

  useEffect(() => {
    let active = true;
    getWorksheet(caseId)
      .then((ws) => {
        if (active) setInitial({ offerPlan: String(ws.offerPlan ?? 0), explanation: ws.explanation ?? "" });
      })
      .catch(() => active && setInitial({ offerPlan: "0", explanation: "" }));
    return () => {
      active = false;
    };
  }, [caseId]);

  if (!initial) return null;

  return (
    <Card className="grid gap-4 p-4">
      <h3 className="font-semibold">Offer plan</h3>
      <Formik initialValues={initial} onSubmit={() => undefined} enableReinitialize>
        <div className="grid gap-4">
          <div className="grid max-w-xs gap-2">
            <FormField
              name="offerPlan"
              label="Offer plan (months of future income)"
              inputMode="numeric"
              placeholder="0"
            />
          </div>
          <FormField name="explanation" label="Explanation" placeholder="Optional narrative" />
          <AutosaveForm
            save={async (patch) => {
              // offerPlan is a whole-number field; coerce before sending.
              if (typeof patch.offerPlan === "string") patch.offerPlan = Number(patch.offerPlan) || 0;
              await patchWorksheet(caseId, patch);
              onChanged();
            }}
          />
        </div>
      </Formik>
    </Card>
  );
}

interface DialogState {
  open: boolean;
  record: TaxDebt | null;
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="gap-1 p-4">
      <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </Card>
  );
}

export function OfferTab({ caseId }: { caseId: string }) {
  const [summary, setSummary] = useState<OfferSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const taxDebts = useCollection(caseId, taxDebtsApi.list);
  const [dialog, setDialog] = useState<DialogState>({ open: false, record: null });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [iaOpen, setIaOpen] = useState(false);

  const reqId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadSummary = useCallback(() => {
    const id = ++reqId.current;
    setSummaryLoading(true);
    setSummaryError(null);
    getOfferSummary(caseId)
      .then((data) => {
        if (!mounted.current || reqId.current !== id) return;
        setSummary(data);
      })
      .catch((err) => {
        if (!mounted.current || reqId.current !== id) return;
        setSummary(null);
        setSummaryError(err instanceof ApiError ? err.message : "Failed to load summary.");
      })
      .finally(() => {
        if (mounted.current && reqId.current === id) setSummaryLoading(false);
      });
  }, [caseId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const reloadAll = useCallback(() => {
    taxDebts.reload();
    loadSummary();
  }, [taxDebts, loadSummary]);

  async function toggleIncluded(row: TaxDebt, included: boolean) {
    await taxDebtsApi.update(caseId, row.id, { included });
    reloadAll();
  }

  const columns: Column<TaxDebt>[] = [
    { header: "Tax Year", cell: (r) => r.taxYear },
    { header: "Amount Owed", cell: (r) => formatMoney(r.amountOwed) },
    { header: "CSED", cell: (r) => r.csed ?? "—" },
    {
      header: "Included",
      cell: (r) => (
        <Checkbox
          checked={r.included}
          aria-label="Include in IA/offer"
          onCheckedChange={(v) => {
            void toggleIncluded(r, v === true);
          }}
        />
      ),
    },
  ];

  return (
    <div className="grid gap-6">
      <CollectionCard
        title="Tax years"
        description="Toggle which years are included in the IA/offer calculation."
        addLabel="Add Tax Year"
        columns={columns}
        rows={taxDebts.rows}
        loading={taxDebts.loading}
        emptyText="No tax years added yet."
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => setDialog({ open: true, record: row })}
        onDelete={async (row) => {
          await taxDebtsApi.remove(caseId, row.id);
          loadSummary();
        }}
      />

      <OfferPlanCard caseId={caseId} onChanged={loadSummary} />

      {summaryLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : summaryError ? (
        <Alert variant="destructive">
          <FiAlertTriangle />
          <AlertTitle>Couldn't load the summary</AlertTitle>
          <AlertDescription>{summaryError}</AlertDescription>
        </Alert>
      ) : summary ? (
        <>
          {summary.warnings.length > 0 && (
            <div className="grid gap-3">
              {summary.warnings.map((warning, i) => {
                const expired = summary.expiredCseds.length > 0 && /expir/i.test(warning);
                return (
                  <Alert key={i} variant={expired ? "destructive" : "default"}>
                    {expired ? <FiAlertTriangle /> : <FiInfo />}
                    <AlertTitle>{expired ? "Attention" : "Notice"}</AlertTitle>
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                );
              })}
            </div>
          )}

          {(summary.missingCseds.length > 0 || summary.expiredCseds.length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {summary.missingCseds.map((year) => (
                <Badge key={`missing-${year}`} variant="secondary">
                  Missing CSED: {year}
                </Badge>
              ))}
              {summary.expiredCseds.map((year) => (
                <Badge key={`expired-${year}`} variant="destructive">
                  Expired CSED: {year}
                </Badge>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <SummaryStat label="Tax due" value={formatMoney(summary.totalDueCents)} />
            <SummaryStat label="RMI" value={formatMoney(summary.rmiCents)} />
            <SummaryStat label="Total assets" value={formatMoney(summary.totalAssetsCents)} />
            <SummaryStat
              label="IA type"
              value={`${summary.iaTypeLabel}${summary.iaCondition ? ` · ${summary.iaCondition}` : ""}`}
            />
            <SummaryStat
              label="Monthly payment"
              value={formatMoney(summary.monthlyPaymentCents)}
            />
            <SummaryStat label="Months" value={String(summary.calculatedMonths)} />
            <SummaryStat label="Interest rate" value={`${summary.interestRate}%`} />
            <SummaryStat label="Offer amount" value={formatMoney(summary.offerAmountCents)} />
            <SummaryStat label="Offer plan months" value={String(summary.offerPlanMonths)} />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => setScheduleOpen(true)}>
              <FiFileText className="size-4" /> Payment Schedule
            </Button>
            <Button type="button" variant="outline" onClick={() => setIaOpen(true)}>
              <FiFileText className="size-4" /> IA Worksheet
            </Button>
          </div>

          <PaymentScheduleDialog
            caseId={caseId}
            summary={summary}
            open={scheduleOpen}
            onOpenChange={setScheduleOpen}
          />
          <IAWorksheetDialog
            caseId={caseId}
            rows={summary.iaWorksheet}
            open={iaOpen}
            onOpenChange={setIaOpen}
          />
        </>
      ) : null}

      <TaxDebtDialog
        caseId={caseId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        onSaved={reloadAll}
      />
    </div>
  );
}
